import { useEffect, useRef } from "react";
import * as Sentry from "@sentry/react";
import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import type { PropertyInfoResponse } from "@/features/propertyCodeAcquisition/types";

// 登記取得専用画面のエラー通知（Sentry 送信 + トースト）の副作用を集約する hook。
// route 本体を「取得→分岐→JSX」に集中させ、エラー通知ロジックを単体テスト可能にする。

/** 物件情報の再取得（取得成功直後の地番マスク解除など）が一時失敗したときのメッセージ。 */
const PROPERTY_REFETCH_FAILED_MESSAGE =
  "物件情報の更新に失敗しました。時間をおいて再度お試しください。";

/**
 * Sentry 送信から除外する「想定内のクライアントエラー」の HTTP ステータス。
 * 401（自動リフレッシュにも失敗した実セッション切れ）・403（権限なし）・404（未存在）は
 * ユーザー・データ起因でありアプリのバグではない。
 */
const SENTRY_EXCLUDED_CLIENT_ERROR_STATUSES: readonly number[] = [
  HttpStatusCode.Unauthorized,
  HttpStatusCode.Forbidden,
  HttpStatusCode.NotFound,
];

const isSentryExcludedClientError = (error: unknown): boolean =>
  isAxiosError(error) &&
  error.response != null &&
  SENTRY_EXCLUDED_CLIENT_ERROR_STATUSES.includes(error.response.status);

/**
 * error の内容（status/message）から安定キーを生成する。参照ではなく内容で同一性を
 * 判定するため、revalidateOnFocus の再フェッチで error 参照が変わっても同種エラーと
 * みなせる。Sentry の重複送信抑止・トーストの再発火抑止の双方で同一性判定に使う。
 */
const errorIdentityKey = (error: unknown): string => {
  if (isAxiosError(error)) {
    return `axios:${error.response?.status ?? "no-response"}:${error.message}`;
  }
  if (error instanceof Error) {
    return `error:${error.message}`;
  }
  return `other:${String(error)}`;
};

/**
 * error が null なら通知済みキーをリセットし、内容キーが前回と異なるときだけ onNew を呼ぶ。
 * 戻り値を呼び出し側の ref に書き戻すことで、Sentry 送信・トースト表示の重複抑止を共通化する。
 */
const notifyIfNewError = (
  error: unknown,
  previousKey: string | null,
  onNew: (error: unknown) => void
): string | null => {
  if (error == null) {
    return null;
  }
  const key = errorIdentityKey(error);
  if (key !== previousKey) {
    onNew(error);
    return key;
  }
  return previousKey;
};

interface UsePropertyAcquisitionErrorReportingParams {
  propertyError: unknown;
  historyError: unknown;
  propertyInfo: PropertyInfoResponse | undefined;
}

/**
 * 物件情報・取得履歴のエラーを Sentry 送信／トースト通知する副作用 hook。
 * - 想定内クライアントエラー（401/403/404）以外の致命的エラーを Sentry へ送る。
 * - データ保持後（propertyInfo 定義済み）の物件情報エラーはトースト通知する。
 */
export const usePropertyAcquisitionErrorReporting = ({
  propertyError,
  historyError,
  propertyInfo,
}: UsePropertyAcquisitionErrorReportingParams): void => {
  // 送信済みの致命的エラーの内容キー（status/message）を保持し、再レンダリングや
  // revalidateOnFocus の再フェッチで error 参照が変わっても同種エラーの重複送信を防ぐ。
  const capturedFatalRef = useRef<{
    property: string | null;
    history: string | null;
  }>({
    property: null,
    history: null,
  });
  // トースト表示済み propertyError の内容キー（status/message）を保持する。
  // revalidateOnFocus の再フェッチで error 参照が変わっても同種エラーの再トーストを防ぐ
  // （propertyInfo 参照だけが変わった際の再発火抑止も兼ねる）。
  const toastedPropertyErrorKeyRef = useRef<string | null>(null);

  // 物件情報・履歴を個別に判定し、両方同時に致命エラーでも片方を取りこぼさない。
  useEffect(() => {
    // 想定外エラーなら未送信時のみ capture し ref に記録する。解消時（null）は ref を戻す。
    const captureFatalIfNew = (
      error: unknown,
      slot: "property" | "history"
    ): void => {
      const fatal =
        error != null && !isSentryExcludedClientError(error) ? error : null;
      capturedFatalRef.current[slot] = notifyIfNewError(
        fatal,
        capturedFatalRef.current[slot],
        Sentry.captureException
      );
    };

    captureFatalIfNew(propertyError, "property");
    captureFatalIfNew(historyError, "history");
  }, [propertyError, historyError]);

  // データ保持中（初回ロード済み）の物件情報エラーはトーストで通知する。
  useEffect(() => {
    if (propertyError == null || propertyInfo !== undefined) {
      toastedPropertyErrorKeyRef.current = notifyIfNewError(
        propertyError,
        toastedPropertyErrorKeyRef.current,
        () => toast.error(PROPERTY_REFETCH_FAILED_MESSAGE)
      );
    }
  }, [propertyError, propertyInfo]);
};
